import { useEffect, useState } from 'react';
import { api, type CatalogLineRow } from '../lib/api';

/**
 * Shared, memoised access to the FP&A line catalog's labels.
 *
 * WHY MEMOISED AT MODULE LEVEL. The consumer is LineCodeMapSelect, which
 * renders once per QBO account row — dozens on the Accounts tab. A useQuery in
 * the component would fire one request per row, because they all mount in the
 * same tick and would all miss a time-based cache. One module-level promise
 * means one request for the page, shared by every row.
 *
 * WHY IT EXISTS AT ALL. The mapping picker's own list of line codes is a
 * hard-coded set in lib/fpaLineCodes, so any code registered in the DB catalog
 * rendered as its raw code — a QBO account mapped to `revenue.var.grants` read
 * as "revenue.var.grants" instead of "Grants received". The catalog is the
 * source of truth for what a line is called; this is how the web reaches it
 * without every surface fetching for itself.
 */

let inflight: Promise<CatalogLineRow[]> | null = null;

function load(): Promise<CatalogLineRow[]> {
  if (!inflight) {
    inflight = api.fpa.catalog()
      .then((r) => r.catalog)
      // Don't cache a failure: the next caller should get a fresh attempt.
      .catch((e) => { inflight = null; throw e; });
  }
  return inflight;
}

/** Drop the memo so the next read refetches — call after a catalog write. */
export function invalidateCatalogLabels(): void {
  inflight = null;
}

export interface CatalogLabels {
  /** The catalog label for a code, or the code itself if it has none. */
  labelOf: (code: string | null) => string;
  /** Active leaves, for a picker. Sorted the way the statement reads. */
  leaves: CatalogLineRow[];
  /** Every row, including groups and retired lines. */
  rows: CatalogLineRow[];
  /**
   * Ancestor labels, outermost first, EXCLUDING the line itself.
   *
   * A mirrored chart produces many leaves with the same label in different
   * branches — nine "Salaries - Sales"-shaped rows, and after two mirror runs
   * the same label three times over. In a flat dropdown they are
   * indistinguishable, so the path is what makes a choice possible.
   */
  ancestorsOf: (code: string) => string[];
  /** The top-level ancestor's label, for grouping a picker's options. */
  rootOf: (code: string) => string;
  loaded: boolean;
}

export function useCatalogLabels(): CatalogLabels {
  const [rows, setRows] = useState<CatalogLineRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    load().then((r) => { if (alive) setRows(r); }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);

  const all = rows ?? [];
  const byCode = new Map(all.map((r) => [r.code, r]));

  // Walk up parent_code. Guarded against a cycle: the catalog's own writes
  // reject one, but a hand-applied migration could still introduce it, and an
  // unguarded walk here would hang the page rather than show a wrong label.
  const ancestorsOf = (code: string): string[] => {
    const out: string[] = [];
    const seen = new Set<string>([code]);
    let cur = byCode.get(code)?.parent_code ?? null;
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      const row = byCode.get(cur);
      if (!row) break;
      out.unshift(row.label);
      cur = row.parent_code;
    }
    return out;
  };

  return {
    labelOf: (code) => (code ? (byCode.get(code)?.label ?? code) : 'Unmapped'),
    leaves: all
      .filter((r) => r.kind === 'leaf' && r.is_active)
      .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
    rows: all,
    ancestorsOf,
    rootOf: (code) => ancestorsOf(code)[0] ?? byCode.get(code)?.label ?? code,
    loaded: rows !== null,
  };
}
